import { UseQueryOptions, UseSuspenseQueryOptions, useMutation, useQueryClient } from '@tanstack/react-query';

import { PaperFilters } from '@/module/Author/Paper/model/PaperTypes';
import { PublicationNewAliasRequest } from '@/module/Author/Publication/model/PublicationTypes';
import { BestQuater, PublicationImpactFactor, PublicationInfluence } from '@/module/Journal';

import { Id, PagedParams, PagedResponse, getQueryKey, invalidateAfterDelete } from '@feature/utils';

import * as api from './ArticleApi';
import { Article, ArticleRequest } from './ArticleTypes';

const articleKey = getQueryKey('article');

export function getArticleImpactFactorOptions(articleId: Id): UseSuspenseQueryOptions<PublicationImpactFactor> {
  return {
    queryKey: articleKey.as([articleId, 'metrics', 'impact-factor']),
    queryFn: () => api.getArticleImpactFactor(articleId),
  };
}

export function getArticleInfluenceOptions(articleId: Id): UseSuspenseQueryOptions<PublicationInfluence> {
  return {
    queryKey: articleKey.as([articleId, 'metrics', 'influence']),
    queryFn: () => api.getArticleInfluence(articleId),
  };
}

export function getArticleBestQ(articleId: Id, bestOf = 5, enabled = false): UseQueryOptions<BestQuater> {
  return {
    queryKey: articleKey.as([articleId, 'best-q']),
    queryFn: () => api.getArticleBestQ(articleId, bestOf),
    enabled: enabled,
  };
}

export function getArticlePagedOptions(
  params: PagedParams<PaperFilters>,
): UseSuspenseQueryOptions<PagedResponse<Article>> {
  return {
    queryKey: articleKey.paged(params),
    queryFn: () => api.getPaged(params),
  };
}

export function getArticleOptions(articleId: Id): UseSuspenseQueryOptions<Article> {
  return {
    queryKey: articleKey.one(articleId),
    queryFn: () => api.get(articleId),
  };
}

export function useArticleEdit(articleId: Id) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: ArticleRequest) => api.edit(articleId, request),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: articleKey.root(),
      }),
  });
}

export function useArticleCreate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: ArticleRequest) => api.create(request),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: articleKey.root(),
      }),
  });
}

export function useArticleDelete(articleId: Id) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.deleteById(articleId),
    onSuccess: () => invalidateAfterDelete(queryClient, articleKey, articleId),
  });
}

export function useArticleAddNewAlias() {
  const queryClient = useQueryClient();
  return useMutation<Article, Error, { articleId: Id; request: PublicationNewAliasRequest }>({
    mutationFn: ({ articleId, request }) => api.addNewAlias(articleId, request),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: articleKey.root(),
      }),
  });
}
